import React, { useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useMasterAuth } from '@/context/MasterAuthContext';
import {
  LayoutDashboard, Globe, BarChart3, LogOut, Menu, CreditCard, X, Shield, Plus, HelpCircle, Palette, Scale,
  Wand2, DollarSign, Server, Store, FileEdit, Image, Settings, Users, Mail, Flag, Webhook, AlertTriangle, ShieldCheck, Calendar, MessageSquare, Trash2
} from 'lucide-react';
import { ENLogo } from '@/components/ENLogo';

export default function MasterLayout() {
  const { user, loading, logout } = useMasterAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [stripeMode, setStripeMode] = React.useState(null);
  const [mode, setMode] = React.useState(() => {
    if (location.pathname.includes('/master/marketplace') || location.pathname.includes('/master/complaints') || location.pathname.includes('/master/signup-errors') || location.pathname.includes('/master/integrations') || location.pathname.includes('/master/users') || location.pathname.includes('/master/transactions') || location.pathname.includes('/master/ein-reviews') || location.pathname.includes('/master/digest') || location.pathname.includes('/master/feedback') || location.pathname.includes('/master/forms') || location.pathname.includes('/master/deleted-accounts')) return 'marketplace';
    return 'website-builder';
  });

  // Sync mode with URL
  React.useEffect(() => {
    if (location.pathname.includes('/master/marketplace') || location.pathname.includes('/master/complaints') || location.pathname.includes('/master/signup-errors') || location.pathname.includes('/master/integrations') || location.pathname.includes('/master/users') || location.pathname.includes('/master/transactions') || location.pathname.includes('/master/ein-reviews') || location.pathname.includes('/master/digest') || location.pathname.includes('/master/feedback') || location.pathname.includes('/master/forms') || location.pathname.includes('/master/deleted-accounts')) setMode('marketplace');
    else if (location.pathname === '/master' || location.pathname.startsWith('/master/sites') || location.pathname.startsWith('/master/ai') || location.pathname.startsWith('/master/billing') || location.pathname.startsWith('/master/analytics') || location.pathname.startsWith('/master/domains') || location.pathname.startsWith('/master/templates') || location.pathname.startsWith('/master/compliance') || location.pathname.startsWith('/master/support')) setMode('website-builder');
  }, [location.pathname]);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/master/login');
    }
  }, [user, loading, navigate]);

  // Fetch Stripe mode for the environment badge
  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem('masterToken');
    if (!token) return;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/master/stripe/status`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setStripeMode(d))
      .catch(() => {});
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0E0E23] via-[#1A1A3E] to-[#0E0E23] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#8B5CF6]/30 border-t-[#8B5CF6] rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) return null;

  const handleLogout = async () => {
    await logout();
    navigate('/master/login');
  };

  const navItems = mode === 'marketplace' ? [
    { path: '/master/marketplace/overview', icon: LayoutDashboard, label: 'Overview', exact: true },
    { path: '/master/marketplace', icon: FileEdit, label: 'Pages Editor', exact: true },
    // Iter 175e — Master Dashboard cleanup. Removed "All Pages" entry;
    // it pointed to the same dataset as "Pages Editor" (`builder_pages` +
    // marketplace cms_pages) and produced a duplicate sidebar slot.
    { path: '/master/marketplace/ai', icon: Wand2, label: 'AI Creator' },
    { path: '/master/users', icon: Users, label: 'Marketplace Users' },
    { path: '/master/builder-sites', icon: Globe, label: 'Builder Signups' },
    { path: '/master/recovery', icon: Globe, label: 'Payment Recovery' },
    { path: '/master/transactions', icon: DollarSign, label: 'Transactions' },
    { path: '/master/ein-reviews', icon: ShieldCheck, label: 'EIN Reviews' },
    { path: '/master/complaints', icon: Flag, label: 'Complaints' },
    { path: '/master/signup-errors', icon: AlertTriangle, label: 'Signup Errors' },
    { path: '/master/digest', icon: Calendar, label: 'Daily Digest' },
    { path: '/master/feedback', icon: MessageSquare, label: 'Feedback' },
    { path: '/master/forms', icon: FileEdit, label: 'Forms' },
    { path: '/master/deleted-accounts', icon: Trash2, label: 'Deleted Accounts' },
    { path: '/master/integrations', icon: Webhook, label: 'Integrations' },
    { path: '/master/email-center', icon: FileEdit, label: 'Email Center' },
  ] : [
    // Iter 175g — Master Dashboard consolidation. Merged:
    //   • "Client Sites" + "Builder Signups" → just "Sites"
    //   • "Billing" + "Billing Controls"     → just "Billing"
    //   • "Overview" + "Analytics"           → just "Overview"
    // Legacy routes remain mounted in App.js so deep links keep working,
    // but they no longer appear as separate sidebar slots.
    { path: '/master', icon: LayoutDashboard, label: 'Overview', exact: true },
    { path: '/master/sites', icon: Globe, label: 'Sites' },
    { path: '/master/recovery', icon: Globe, label: 'Payment Recovery' },
    { path: '/master/ai-redesign', icon: Wand2, label: 'AI Redesign' },
    { path: '/master/billing', icon: CreditCard, label: 'Billing' },
    { path: '/master/domains', icon: Server, label: 'Domains' },
    { path: '/master/templates', icon: Palette, label: 'Templates' },
    { path: '/master/compliance', icon: Scale, label: 'Compliance' },
    { path: '/master/email-center', icon: Mail, label: 'Email Center' },
    { path: '/master/support', icon: HelpCircle, label: 'Support' },
  ];

  const isActive = (path, exact) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0E0E23] via-[#1A1A3E] to-[#0E0E23] text-white font-sans" data-testid="master-dashboard">
      {/* Background grid */}
      <div className="fixed inset-0 opacity-[0.03] pointer-events-none" style={{
        backgroundImage: 'linear-gradient(rgba(139,92,246,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(139,92,246,0.3) 1px, transparent 1px)',
        backgroundSize: '60px 60px'
      }} />

      {/* Mobile toggle */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="fixed top-4 left-4 z-[60] lg:hidden bg-[#1E1E3A]/90 backdrop-blur-xl border border-white/10 p-2.5 rounded-xl text-white shadow-lg"
        data-testid="master-mobile-menu"
      >
        {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
      </button>

      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-0 h-screen w-[260px] bg-[#1E1E3A]/80 backdrop-blur-2xl border-r border-white/5 flex flex-col z-50 transition-transform duration-300 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
        data-testid="master-sidebar"
      >
        {/* Logo */}
        <div className="px-6 pt-7 pb-4">
          <ENLogo size="md" linkTo="/master" />
          <p className="text-[11px] text-[#9CA3AF] mt-1">Super Admin</p>
          {stripeMode && (
            <div
              className={`mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border ${
                stripeMode.mode === 'live'
                  ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300 shadow-[0_0_12px_rgba(16,185,129,0.25)]'
                  : stripeMode.mode === 'test'
                  ? 'bg-amber-500/15 border-amber-500/40 text-amber-300'
                  : 'bg-white/5 border-white/10 text-[#9CA3AF]'
              }`}
              title={`Stripe ${stripeMode.mode} · key ${stripeMode.key_prefix || '—'}${stripeMode.webhook_configured ? '' : ' · webhook MISSING'}`}
              data-testid="stripe-mode-badge"
            >
              <span className={`w-1.5 h-1.5 rounded-full ${
                stripeMode.mode === 'live' ? 'bg-emerald-400 animate-pulse' : stripeMode.mode === 'test' ? 'bg-amber-400' : 'bg-[#64748B]'
              }`} />
              {stripeMode.mode === 'live' ? 'Live Mode' : stripeMode.mode === 'test' ? 'Sandbox' : 'Unknown'}
              {!stripeMode.webhook_configured && <span className="text-red-300 ml-1">· hook?</span>}
            </div>
          )}
        </div>

        {/* Mode Toggle */}
        <div className="px-4 mb-4">
          <div className="bg-[#0E0E23]/60 rounded-xl p-1 flex gap-1" data-testid="master-mode-toggle">
            <button
              onClick={() => { setMode('marketplace'); navigate('/master/marketplace'); }}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                mode === 'marketplace'
                  ? 'bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white shadow-[0_0_12px_rgba(139,92,246,0.3)]'
                  : 'text-[#9CA3AF] hover:text-white'
              }`}
              data-testid="mode-marketplace-btn"
            >
              <Store size={14} /> Marketplace
            </button>
            <button
              onClick={() => { setMode('website-builder'); navigate('/master'); }}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                mode === 'website-builder'
                  ? 'bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white shadow-[0_0_12px_rgba(139,92,246,0.3)]'
                  : 'text-[#9CA3AF] hover:text-white'
              }`}
              data-testid="mode-website-builder-btn"
            >
              <Globe size={14} /> Web Builder
            </button>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            const active = isActive(item.path, item.exact);
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-medium transition-all duration-200 group relative ${
                  active
                    ? 'text-white bg-gradient-to-r from-[#8B5CF6]/30 to-[#3B82F6]/20 shadow-[0_0_20px_rgba(139,92,246,0.2)]'
                    : 'text-[#9CA3AF] hover:text-white hover:bg-white/5'
                }`}
                data-testid={`master-nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                {active && <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-gradient-to-b from-[#8B5CF6] to-[#3B82F6] rounded-r-full" />}
                <item.icon size={18} strokeWidth={active ? 2 : 1.5} className={active ? 'text-[#A78BFA]' : 'group-hover:text-white'} />
                {item.label}
              </Link>
            );
          })}
        </nav>

        {/* User / Logout */}
        <div className="p-4 border-t border-white/5">
          <div className="flex items-center gap-3 px-3 py-2">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#8B5CF6] to-[#3B82F6] flex items-center justify-center text-sm font-bold shadow-[0_0_10px_rgba(139,92,246,0.3)]">
              M
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-white font-medium truncate" data-testid="master-user-name">Master Admin</p>
              <p className="text-[11px] text-[#9CA3AF]">Platform Owner</p>
            </div>
            <button onClick={handleLogout} className="text-[#9CA3AF] hover:text-[#FF6B6B] transition-colors" data-testid="master-logout-btn">
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/60 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Main Content */}
      <main className="lg:ml-[260px] min-h-screen relative">
        {/* Top Bar */}
        <div className="sticky top-0 z-30 bg-[#0E0E23]/60 backdrop-blur-xl border-b border-white/5 px-6 sm:px-8 lg:px-10 py-4 flex items-center justify-end">
          <Link
            to="/master/sites/new"
            className="hidden sm:flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white shadow-[0_0_15px_rgba(139,92,246,0.3)] hover:shadow-[0_0_25px_rgba(139,92,246,0.5)] transition-all"
            data-testid="create-site-btn"
          >
            <Plus size={16} /> New Site
          </Link>
        </div>

        <div className="p-6 sm:p-8 lg:p-10" data-testid="master-content">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
